import React from 'react';
import { Target, Eye, Award, Lightbulb, Shield, Users2 } from 'lucide-react';
import { aboutData, missionVision } from '../data/mock';

const About = () => {
  const valueIcons = {
    "Desire for Excellence": Award,
    "Trust & Confidence": Shield,
    "Innovation": Lightbulb,
    "Transparency": Eye,
    "Teamwork": Users2
  };

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* About Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={aboutData.image} 
                alt="Trayaksh Technology Team"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-cyan-500 to-lime-500 text-white p-6 rounded-2xl shadow-2xl">
              <div className="text-3xl font-bold">15+</div>
              <div className="text-sm font-semibold">Years of Excellence</div>
            </div>
          </div>

          {/* Content */}
          <div>
            <div className="inline-block px-4 py-2 bg-cyan-100 rounded-full mb-4">
              <span className="text-cyan-700 font-semibold text-sm">About Us</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {aboutData.title}
            </h2>
            
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {aboutData.description}
            </p>
            
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              {aboutData.extendedDescription}
            </p>

            <div className="bg-gradient-to-r from-cyan-50 to-lime-50 border-l-4 border-cyan-500 p-6 rounded-r-xl">
              <p className="text-gray-800 font-medium italic">
                "Our team operates with enthusiasm and flexibility. We are focused on our customers, being their reliable partner for IT services and solutions."
              </p>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {/* Mission */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl">
                <Target className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {missionVision.mission.title}
              </h3>
            </div>
            <p className="text-gray-700 mb-6 leading-relaxed">
              {missionVision.mission.description}
            </p>
            <ul className="space-y-3">
              {missionVision.mission.points.map((point, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="mt-1 p-1 bg-cyan-100 rounded-full">
                    <div className="w-2 h-2 bg-cyan-600 rounded-full"></div>
                  </div>
                  <span className="text-gray-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Vision */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-lime-500 to-lime-600 rounded-xl">
                <Eye className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {missionVision.vision.title}
              </h3>
            </div>
            <p className="text-gray-700 mb-6 leading-relaxed">
              {missionVision.vision.description}
            </p>
            <ul className="space-y-3">
              {missionVision.vision.points.map((point, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="mt-1 p-1 bg-lime-100 rounded-full">
                    <div className="w-2 h-2 bg-lime-600 rounded-full"></div>
                  </div>
                  <span className="text-gray-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Core Values */}
        <div>
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-lime-100 rounded-full mb-4">
              <span className="text-lime-700 font-semibold text-sm">Our Values</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Keys for Development
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide our every action and decision
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {aboutData.values.map((value, index) => {
              const IconComponent = valueIcons[value.title];
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group"
                >
                  <div className="mb-4 p-3 bg-gradient-to-br from-cyan-100 to-lime-100 rounded-lg inline-block group-hover:scale-110 transition-transform duration-300">
                    {IconComponent && <IconComponent className="text-cyan-700" size={28} />}
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    {value.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
